import { useQuery } from '@tanstack/react-query';
import { SearchIcon } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/app/api';
import type { MatterSearchResult } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { StatusChip } from '@/components/matter/StatusChip';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Kbd } from '@/components/ui/kbd';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { shortDate } from '@/lib/format';
import { useShell } from './context';

const RECENT_KEY = 'beam.recentMatters';
const MIN_CHARS = 2;
const DEBOUNCE_MS = 150;
const MAX_RESULTS = 8;

export function readRecent(): MatterSearchResult[] {
  try {
    const raw = window.sessionStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as MatterSearchResult[]) : [];
  } catch {
    return [];
  }
}

/** The matter hub records each opened matter so an empty search shows the five most recent (per tab). */
export function recordRecent(m: MatterSearchResult): void {
  try {
    const next = [m, ...readRecent().filter((r) => r.ref !== m.ref)].slice(0, 5);
    window.sessionStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable: recents are a convenience only.
  }
}

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/** The desktop trigger on the ink bar: looks like the input it opens. */
export function SearchTrigger({ className }: { className?: string }) {
  const { setSearchOpen } = useShell();
  return (
    <button
      type="button"
      onClick={() => setSearchOpen(true)}
      aria-label="Search case or patient"
      aria-keyshortcuts="/ Control+K Meta+K"
      className={`flex h-8 w-[280px] items-center gap-2 rounded-md border border-white/20 bg-white/10 px-2.5 text-left text-[13px] text-beam-on-ink-muted hover:bg-white/15 ${className ?? ''}`}
    >
      <SearchIcon aria-hidden="true" className="size-4" />
      <span className="flex-1">Search case or patient</span>
      <Kbd className="bg-white/10 text-beam-on-ink-muted">/</Kbd>
    </button>
  );
}

function Results({ onPick, inputRef }: { onPick: (m: MatterSearchResult) => void; inputRef: RefObject<HTMLInputElement | null> }) {
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), DEBOUNCE_MS);
  const navigate = useNavigate();
  const enabled = q.length >= MIN_CHARS;
  const search = useQuery({
    queryKey: ['matterSearch', q],
    queryFn: () => api<MatterSearchResult[]>(`/api/matters/search?q=${encodeURIComponent(q)}`),
    enabled,
    staleTime: 30_000,
  });
  const recent = readRecent();
  const results = enabled ? (search.data ?? []).slice(0, MAX_RESULTS) : recent;
  const heading = enabled ? 'Cases' : recent.length > 0 ? 'Recently opened' : undefined;

  return (
    <Command shouldFilter={false} label="Case search">
      <CommandInput ref={inputRef} value={query} onValueChange={setQuery} placeholder="Reference or surname" className="text-base md:text-sm" />
      <CommandList>
        {enabled && !search.isPending && results.length === 0 ? (
          <CommandEmpty>No matter matches &lsquo;{q}&rsquo;. Check the reference or try the surname.</CommandEmpty>
        ) : null}
        {!enabled && recent.length === 0 ? <CommandEmpty>Type at least two characters.</CommandEmpty> : null}
        {results.length > 0 ? (
          <CommandGroup heading={heading}>
            {results.map((m) => (
              <CommandItem key={m.ref} value={m.ref} onSelect={() => onPick(m)} className="min-h-11 gap-3 md:min-h-9">
                <span className="tabular w-32 shrink-0 font-semibold">{m.ref}</span>
                <span className="min-w-0 flex-1 truncate">{m.patientAsOnLoi}</span>
                <span className="hidden w-28 shrink-0 truncate text-beam-text-muted sm:inline">{m.doctor.name}</span>
                <StatusChip status={m.status} size="sm" />
                <span className="tabular w-14 shrink-0 text-right text-xs text-beam-text-muted">{shortDate(m.dueDate)}</span>
              </CommandItem>
            ))}
            {enabled && search.data && search.data.length > MAX_RESULTS ? (
              <CommandItem value="__all" onSelect={() => void navigate(`/pipeline?q=${encodeURIComponent(q)}`)} className="text-beam-signature">
                Show all matching in Pipeline
              </CommandItem>
            ) : null}
          </CommandGroup>
        ) : null}
      </CommandList>
      <p className="border-t border-beam-line px-3 py-2 text-xs text-beam-text-muted">Search by reference (WC-2026-09130 or 9130) or surname.</p>
    </Command>
  );
}

/**
 * The matter search palette: "/" outside editable fields and Ctrl or Cmd
 * plus K open it; a Sheet on the phone, a 640 px dialog on the desktop.
 */
export function MatterSearch() {
  const { searchOpen, setSearchOpen } = useShell();
  const phone = useIsPhone();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Radix moves focus to the first tabbable on open; the input is named explicitly so the two never race.
  const focusInput = (e: Event) => {
    e.preventDefault();
    inputRef.current?.focus();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isEditable(e.target) && !document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
        // Not over an open reason form or confirmation.
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSearchOpen]);

  const pick = (m: MatterSearchResult) => {
    recordRecent(m);
    setSearchOpen(false);
    void navigate(`/matters/${m.ref}`);
  };

  if (phone) {
    return (
      <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
        <SheetContent side="bottom" className="h-[100svh] rounded-none p-0" showCloseButton onOpenAutoFocus={focusInput}>
          <SheetHeader className="pb-0">
            <SheetTitle>Search</SheetTitle>
            <SheetDescription className="sr-only">Find a case by reference or surname</SheetDescription>
          </SheetHeader>
          {searchOpen ? <Results onPick={pick} inputRef={inputRef} /> : null}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent showCloseButton={false} onOpenAutoFocus={focusInput} className="top-[20%] w-[640px] max-w-[calc(100vw-2rem)] translate-y-0 gap-0 overflow-hidden p-0 shadow-beam-3 sm:max-w-[640px]">
        <DialogHeader className="sr-only">
          <DialogTitle>Case search</DialogTitle>
          <DialogDescription>Find a case by reference or surname</DialogDescription>
        </DialogHeader>
        {searchOpen ? <Results onPick={pick} inputRef={inputRef} /> : null}
      </DialogContent>
    </Dialog>
  );
}
